import { Icon } from '@/components/Icon';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface OrgSuspendedProps {
  email: string | undefined;
  requestId: string | undefined;
  onSignOut: () => void;
  /** An operator previewing a suspended client (should the API refuse the preview too): leave the preview instead. */
  preview?: { name: string; onExit: () => void };
}

/**
 * The API refused the org itself with 403 `HGR-ORG-SUSPENDED` (SPEC §17.6): a platform operator suspended it. One
 * clear page with sign out, not a generic error, and nothing to retry: only Malible can lift it. The reason the
 * operator wrote is never shown to the client.
 */
export function OrgSuspended({ email, requestId, onSignOut, preview }: OrgSuspendedProps) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4 sm:p-6">
      <Card role="alert" data-testid="org-suspended" className="w-full max-w-[460px] rounded-xl p-6 shadow-overlay">
        <Logo height={24} />
        <div className="mt-5 flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-awaiting/30 bg-awaiting/10 text-awaiting">
            <Icon name="pause_circle" size={22} />
          </span>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-foreground">
              {preview ? `${preview.name}'s access to Hangar is suspended` : "Your organization's access to Hangar is suspended"}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {preview
                ? 'Its team cannot sign in, and its API and CI tokens are refused. Reactivate it from Clients.'
                : 'Contact your Hangar administrators to restore it. Your projects, builds and settings are kept as they were.'}
            </p>
            {email && !preview ? <p className="mt-2 break-all text-xs text-subtle">Signed in as {email}</p> : null}
            {requestId ? <p className="mt-1 font-mono text-xs text-subtle">{requestId}</p> : null}
          </div>
        </div>
        {preview ? (
          <Button data-testid="org-suspended-exit-preview" className="mt-6 min-h-11 w-full" onClick={preview.onExit}>
            Exit preview
          </Button>
        ) : (
          <Button data-testid="org-suspended-sign-out" className="mt-6 min-h-11 w-full" onClick={onSignOut}>
            Sign out
          </Button>
        )}
      </Card>
    </div>
  );
}
